import ClassNames from "classnames";
import { FunctionComponent, useEffect, useState } from "react";
import { getActiveSite } from "../../selectors/sites.js";
import { injectScript, loadScript } from "../../slices/loadStates.js";
import { useAppDispatch, useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/gastro-pool-insurance-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  BaseModuleProps,
  GastroPoolInsuranceModuleSettings,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getSupportedLanguage,
} from "../../utils/utils.js";
import LazyloadWrapper from "../LazyloadWrapper.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

type Props = BaseModuleProps<GastroPoolInsuranceModuleSettings>;

const GastroPoolInsuranceModule: FunctionComponent<Props> = ({
  translatedModule: {
    id,
    translation: {
      languageId,
      settings: { title, subtitle, key },
    },
    settings: { textAlign },
  },
  translatedModule,
  isFirstOnPage,
}) => {
  const dispatch = useAppDispatch();
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const [isLazyloaded, setIsLazyloaded] = useState(false);

  const scriptLanguage = getSupportedLanguage(languageId, ["de", "it", "en"]);
  const scriptUrl = `https://portal.gastropool.it/rxtravel/widget/${scriptLanguage}?${key}`;

  useEffect(() => {
    isLazyloaded && key && dispatch(injectScript(scriptUrl));
  }, [scriptLanguage, key, isLazyloaded]);

  // The script needs to be loaded again and the widget script removed,
  // otherwise the widget won’t load again when navigating pages in the CMS
  useEffect(
    () => () => {
      dispatch(loadScript({ url: scriptUrl, status: "unloaded" }));
    },
    [],
  );

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      className={styles.GastroPoolInsuranceModule}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>

      <LazyloadWrapper onLoad={setIsLazyloaded}>
        <div
          className={ClassNames(moduleBaseStyles.Module__Wrapper, {
            [styles["GastroPoolInsuranceModule__Content--empty"]]: !key,
          })}
        >
          {key && (
            <div id="rxtravel-container">
              <div id="ti-form">
                <div
                  className={ClassNames(
                    "block",
                    styles.GastroPoolInsuranceModule__Center,
                  )}
                >
                  <img src="https://portal.gastropool.it/app_themes/travel/loader.gif" />
                </div>
              </div>
            </div>
          )}
        </div>
      </LazyloadWrapper>
    </ModuleWithHeadings>
  );
};

export default GastroPoolInsuranceModule;
